/**
 * CompletionContext
 * Global state management for goal completions with optimistic updates
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CompletionEntry } from '../models/CompletionEntry';
import {
  replayCompletionEvents,
  markGoalComplete,
} from '../services/storage/CompletionLog';
import { getTodayDateString } from '../utils/dateUtils';
import { performanceMonitor } from '../utils/PerformanceMonitor';

interface CompletionContextType {
  // Completion state (goalId -> date -> CompletionEntry)
  completions: Map<string, Map<string, CompletionEntry>>;

  // Loading state
  isLoading: boolean;

  // Operations
  toggleCompletion: (accountId: string, goalId: string, date: string) => Promise<void>;
  markComplete: (accountId: string, goalId: string, date: string) => Promise<void>;
  markUncomplete: (accountId: string, goalId: string, date: string) => Promise<void>;
  getCompletion: (goalId: string, date: string) => CompletionEntry | undefined;
  isCompleted: (goalId: string, date: string) => boolean;
  loadCompletionsForGoal: (accountId: string, goalId: string) => Promise<void>;
  loadCompletionsForDateRange: (accountId: string, goalId: string, dates: string[]) => Promise<void>;
  refreshAllCompletions: (accountId: string, goalIds: string[]) => Promise<void>;
}

const CompletionContext = createContext<CompletionContextType | undefined>(undefined);

interface CompletionProviderProps {
  children: ReactNode;
}

export const CompletionProvider: React.FC<CompletionProviderProps> = ({ children }) => {
  // Map structure: goalId -> (date -> CompletionEntry)
  const [completions, setCompletions] = useState<Map<string, Map<string, CompletionEntry>>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Toggle completion status with optimistic update
   */
  const toggleCompletion = async (accountId: string, goalId: string, date: string): Promise<void> => {
    performanceMonitor.start(`toggle_completion_${date}`);

    // Store the previous state for rollback
    let previousState: CompletionEntry | undefined;

    // Optimistic update - update UI immediately using functional update
    setCompletions(prevCompletions => {
      const goalCompletions = prevCompletions.get(goalId) || new Map();
      const existing = goalCompletions.get(date);
      previousState = existing;
      const newCompletedState = !(existing?.completed ?? false);

      const optimisticEntry: CompletionEntry = {
        id: existing?.id || `temp-${Date.now()}`,
        accountId,
        goalId,
        date,
        completed: newCompletedState,
        timestamp: Date.now(),
        syncStatus: 'local',
      };

      const updatedGoalCompletions = new Map(goalCompletions);
      updatedGoalCompletions.set(date, optimisticEntry);

      const updatedCompletions = new Map(prevCompletions);
      updatedCompletions.set(goalId, updatedGoalCompletions);

      console.log(`[CompletionContext] Optimistically toggled ${goalId} for ${date} to ${newCompletedState}`);
      return updatedCompletions;
    });

    performanceMonitor.end(`toggle_completion_${date}`);

    // Persist in background
    try {
      const newCompletedState = !(previousState?.completed ?? false);
      await markGoalComplete(accountId, goalId, date, newCompletedState);

      // Refresh from storage to get actual persisted state
      await loadCompletionsForGoal(accountId, goalId);
    } catch (error) {
      console.error('Failed to persist completion:', error);

      // Rollback optimistic update on error using functional update
      setCompletions(prevCompletions => {
        const goalCompletions = prevCompletions.get(goalId) || new Map();
        const rolledBackGoalCompletions = new Map(goalCompletions);

        if (previousState) {
          rolledBackGoalCompletions.set(date, previousState);
        } else {
          rolledBackGoalCompletions.delete(date);
        }

        const rolledBackCompletions = new Map(prevCompletions);
        rolledBackCompletions.set(goalId, rolledBackGoalCompletions);
        return rolledBackCompletions;
      });
    }
  };

  /**
   * Mark goal as complete for a specific date
   */
  const markComplete = async (accountId: string, goalId: string, date: string): Promise<void> => {
    performanceMonitor.start(`mark_complete_${date}`);

    // Store previous state for rollback
    let previousState: CompletionEntry | undefined;

    // Optimistic update using functional update
    setCompletions(prevCompletions => {
      const goalCompletions = prevCompletions.get(goalId) || new Map();
      const existing = goalCompletions.get(date);
      previousState = existing;

      const optimisticEntry: CompletionEntry = {
        id: existing?.id || `temp-${Date.now()}`,
        accountId,
        goalId,
        date,
        completed: true,
        timestamp: Date.now(),
        syncStatus: 'local',
      };

      const updatedGoalCompletions = new Map(goalCompletions);
      updatedGoalCompletions.set(date, optimisticEntry);

      const updatedCompletions = new Map(prevCompletions);
      updatedCompletions.set(goalId, updatedGoalCompletions);

      return updatedCompletions;
    });

    performanceMonitor.end(`mark_complete_${date}`);

    // Persist in background
    try {
      await markGoalComplete(accountId, goalId, date, true);
      await loadCompletionsForGoal(accountId, goalId);
    } catch (error) {
      console.error('Failed to mark complete:', error);

      // Rollback using functional update
      setCompletions(prevCompletions => {
        const goalCompletions = prevCompletions.get(goalId) || new Map();
        const rolledBackGoalCompletions = new Map(goalCompletions);

        if (previousState) {
          rolledBackGoalCompletions.set(date, previousState);
        } else {
          rolledBackGoalCompletions.delete(date);
        }

        const rolledBackCompletions = new Map(prevCompletions);
        rolledBackCompletions.set(goalId, rolledBackGoalCompletions);
        return rolledBackCompletions;
      });
    }
  };

  /**
   * Mark goal as uncomplete for a specific date
   */
  const markUncomplete = async (accountId: string, goalId: string, date: string): Promise<void> => {
    performanceMonitor.start(`mark_uncomplete_${date}`);

    // Store previous state for rollback
    let previousState: CompletionEntry | undefined;

    // Optimistic update using functional update
    setCompletions(prevCompletions => {
      const goalCompletions = prevCompletions.get(goalId) || new Map();
      const existing = goalCompletions.get(date);
      previousState = existing;

      const optimisticEntry: CompletionEntry = {
        id: existing?.id || `temp-${Date.now()}`,
        accountId,
        goalId,
        date,
        completed: false,
        timestamp: Date.now(),
        syncStatus: 'local',
      };

      const updatedGoalCompletions = new Map(goalCompletions);
      updatedGoalCompletions.set(date, optimisticEntry);

      const updatedCompletions = new Map(prevCompletions);
      updatedCompletions.set(goalId, updatedGoalCompletions);

      return updatedCompletions;
    });

    performanceMonitor.end(`mark_uncomplete_${date}`);

    // Persist in background
    try {
      await markGoalComplete(accountId, goalId, date, false);
      await loadCompletionsForGoal(accountId, goalId);
    } catch (error) {
      console.error('Failed to mark uncomplete:', error);

      // Rollback using functional update
      setCompletions(prevCompletions => {
        const goalCompletions = prevCompletions.get(goalId) || new Map();
        const rolledBackGoalCompletions = new Map(goalCompletions);

        if (previousState) {
          rolledBackGoalCompletions.set(date, previousState);
        } else {
          rolledBackGoalCompletions.delete(date);
        }

        const rolledBackCompletions = new Map(prevCompletions);
        rolledBackCompletions.set(goalId, rolledBackGoalCompletions);
        return rolledBackCompletions;
      });
    }
  };

  /**
   * Get completion entry for a specific goal and date
   */
  const getCompletion = (goalId: string, date: string): CompletionEntry | undefined => {
    const goalCompletions = completions.get(goalId);
    return goalCompletions?.get(date);
  };

  /**
   * Check if goal is completed for a specific date
   */
  const isCompleted = (goalId: string, date: string): boolean => {
    const completion = getCompletion(goalId, date);
    const result = completion?.completed ?? false;
    // Only log when checking today's date to reduce noise
    const today = new Date().toISOString().split('T')[0];
    if (date === today) {
      console.log(`[CompletionContext] isCompleted(${goalId}, ${date}) = ${result}`, completion);
    }
    return result;
  };

  /**
   * Load all completions for a specific goal
   */
  const loadCompletionsForGoal = async (accountId: string, goalId: string): Promise<void> => {
    try {
      setIsLoading(true);

      const completionMap = await replayCompletionEvents(accountId, goalId);

      // Update state using functional update to avoid stale closures
      setCompletions(prevCompletions => {
        const updatedCompletions = new Map(prevCompletions);
        updatedCompletions.set(goalId, completionMap);
        return updatedCompletions;
      });
    } catch (error) {
      console.error('Failed to load completions for goal:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load completions for a specific date range
   * Note: We load ALL completions for the goal (not just the date range)
   * to avoid creating fake uncompleted entries and to maintain accurate state
   */
  const loadCompletionsForDateRange = async (
    accountId: string,
    goalId: string,
    dates: string[]
  ): Promise<void> => {
    try {
      setIsLoading(true);

      // Use replayCompletionEvents to get all real completion data
      // This avoids polluting state with fake "uncompleted" entries
      const completionMap = await replayCompletionEvents(accountId, goalId);

      // Update state using functional update to avoid stale closures
      setCompletions(prevCompletions => {
        const updatedCompletions = new Map(prevCompletions);
        updatedCompletions.set(goalId, completionMap);
        console.log(`[CompletionContext] Loaded ${completionMap.size} completion entries for goal ${goalId}`);
        return updatedCompletions;
      });
    } catch (error) {
      console.error('Failed to load completions for date range:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh all completions for multiple goals
   */
  const refreshAllCompletions = async (accountId: string, goalIds: string[]): Promise<void> => {
    try {
      setIsLoading(true);

      // Load all completions in parallel
      const completionPromises = goalIds.map(goalId =>
        replayCompletionEvents(accountId, goalId).then(completionMap => ({
          goalId,
          completionMap
        }))
      );

      const results = await Promise.all(completionPromises);

      // Update state using functional update to avoid stale closures
      setCompletions(prevCompletions => {
        const updatedCompletions = new Map(prevCompletions);
        results.forEach(({ goalId, completionMap }) => {
          updatedCompletions.set(goalId, completionMap);
        });
        return updatedCompletions;
      });
    } catch (error) {
      console.error('Failed to refresh completions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const value: CompletionContextType = {
    completions,
    isLoading,
    toggleCompletion,
    markComplete,
    markUncomplete,
    getCompletion,
    isCompleted,
    loadCompletionsForGoal,
    loadCompletionsForDateRange,
    refreshAllCompletions,
  };

  return <CompletionContext.Provider value={value}>{children}</CompletionContext.Provider>;
};

/**
 * Hook to use CompletionContext
 */
export const useCompletionContext = (): CompletionContextType => {
  const context = useContext(CompletionContext);
  if (!context) {
    throw new Error('useCompletionContext must be used within a CompletionProvider');
  }
  return context;
};
